import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, can, getUser, heldForApproval, type Approval } from '../lib/api';
import { qrMatrix, qrSvgPath } from '../lib/qr';
import { ErrorNote, Field, Modal, useToast } from './ui';
import '../styles/controls.css';

/**
 * Console controls: two-factor sign-in, reveal with a reason, and the
 * "sent for approval" state of a change held for a second person.
 */

// ---------------------------------------------------------------- QR and recovery codes

/** Drawn here from the otpauth URI. The secret never goes to an outside service. */
export function QrCode({ text, size = 184 }: { text: string; size?: number }) {
  const { path, size: n } = useMemo(() => qrSvgPath(qrMatrix(text)), [text]);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${n} ${n}`} role="img" aria-label="QR code for the authenticator app"
      style={{ background: '#fff', borderRadius: 6, display: 'block' }} shapeRendering="crispEdges">
      <path d={path} fill="#000" />
    </svg>
  );
}

function groupSecret(secret: string): string {
  return secret.replace(/(.{4})/g, '$1 ').trim();
}

export function RecoveryCodes({ codes }: { codes: string[] }) {
  const toast = useToast();
  return (
    <div className="note warn" style={{ marginTop: 12 }}>
      <strong>Recovery codes</strong>
      <div className="small" style={{ marginTop: 4 }}>
        Shown once. Each code signs you in once if the phone is not to hand. Keep them somewhere safe, away from the phone.
      </div>
      <div className="mono" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, max-content)', gap: '4px 24px', margin: '10px 0' }}>
        {codes.map((c) => <span key={c}>{c}</span>)}
      </div>
      <button
        className="small"
        onClick={() => navigator.clipboard.writeText(codes.join('\n')).then(
          () => toast('Recovery codes copied', 'ok'), () => toast('Could not copy. Select them manually.', 'bad'),
        )}
      >
        Copy codes
      </button>
    </div>
  );
}

/** The enrolment body shared by sign-in and the user menu: scan, or type the key, then the first code. */
export function EnrolSteps({
  secret, otpauthUri, code, setCode,
}: { secret: string; otpauthUri: string; code: string; setCode: (v: string) => void }) {
  return (
    <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <QrCode text={otpauthUri} />
      <div style={{ flex: '1 1 220px', minWidth: 0 }}>
        <ol className="small" style={{ paddingLeft: 18, marginTop: 0 }}>
          <li>Open an authenticator app on your phone.</li>
          <li>Scan the code, or enter the key below.</li>
          <li>Type the six digits the app shows.</li>
        </ol>
        <div className="small muted">Key</div>
        <div className="mono" style={{ wordBreak: 'break-all', marginBottom: 12 }}>{groupSecret(secret)}</div>
        <Field label="Code from the app">
          <input
            inputMode="numeric" pattern="[0-9]*" maxLength={6} autoComplete="one-time-code" autoFocus
            value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          />
        </Field>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- user menu: two-factor sign-in

interface MfaState { enrolled: boolean; confirmedAt: string | null; recoveryCodesLeft: number; required: boolean }

export function TwoFactorDialog({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const [state, setState] = useState<MfaState | null>(null);
  const [setup, setSetup] = useState<{ secret: string; otpauthUri: string } | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [mode, setMode] = useState<'view' | 'disable' | 'regenerate'>('view');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api.get<MfaState>('/v1/me/mfa').then(setState).catch((e) => setError((e as Error).message));
  }, []);
  useEffect(load, [load]);

  async function run(fn: () => Promise<void>) {
    setBusy(true); setError(null);
    try { await fn(); } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }

  const start = () => run(async () => { setSetup(await api.post('/v1/me/mfa/start')); setCode(''); });
  const confirm = () => run(async () => {
    const res = await api.post<{ recoveryCodes: string[] }>('/v1/me/mfa/confirm', { code });
    setCodes(res.recoveryCodes); setSetup(null); setCode(''); load();
    toast('Two-factor sign-in is on.', 'ok');
  });
  const disable = () => run(async () => {
    await api.post('/v1/me/mfa/disable', { code });
    setMode('view'); setCode(''); load();
    toast('Two-factor sign-in is off.', 'ok');
  });
  const regenerate = () => run(async () => {
    const res = await api.post<{ recoveryCodes: string[] }>('/v1/me/mfa/recovery-codes', { code });
    setCodes(res.recoveryCodes); setMode('view'); setCode(''); load();
  });

  let body: ReactNode = null;
  let footer: ReactNode = <button onClick={onClose}>Close</button>;
  if (!state) {
    body = <div className="muted small">Loading…</div>;
  } else if (codes) {
    body = <RecoveryCodes codes={codes} />;
    footer = <button className="primary" onClick={() => setCodes(null)}>I have saved the codes</button>;
  } else if (setup) {
    body = <EnrolSteps secret={setup.secret} otpauthUri={setup.otpauthUri} code={code} setCode={setCode} />;
    footer = (
      <>
        <button onClick={() => setSetup(null)}>Cancel</button>
        <button className="primary" onClick={confirm} disabled={busy || code.length !== 6}>{busy ? 'Checking…' : 'Turn on'}</button>
      </>
    );
  } else if (mode !== 'view') {
    body = (
      <Field label="Code from the app, or a recovery code">
        <input value={code} autoFocus autoComplete="one-time-code" onChange={(e) => setCode(e.target.value.trim())} />
      </Field>
    );
    footer = (
      <>
        <button onClick={() => { setMode('view'); setCode(''); }}>Cancel</button>
        {mode === 'disable'
          ? <button className="danger" onClick={disable} disabled={busy || code.length < 6}>Turn off</button>
          : <button className="primary" onClick={regenerate} disabled={busy || code.length < 6}>Issue new codes</button>}
      </>
    );
  } else if (state.enrolled) {
    body = (
      <dl className="small" style={{ margin: 0 }}>
        <dt className="muted">Status</dt><dd>On since {state.confirmedAt ? new Date(state.confirmedAt).toLocaleDateString() : 'enrolment'}</dd>
        <dt className="muted">Recovery codes left</dt><dd>{state.recoveryCodesLeft} of 10</dd>
        {state.required && <><dt className="muted">Organisation setting</dt><dd>Required for your role</dd></>}
      </dl>
    );
    footer = (
      <>
        <button onClick={onClose}>Close</button>
        <button onClick={() => setMode('regenerate')}>New recovery codes</button>
        {!state.required && <button className="danger" onClick={() => setMode('disable')}>Turn off</button>}
      </>
    );
  } else {
    body = (
      <p className="small" style={{ marginTop: 0 }}>
        Off. With it on, each sign-in also asks for a six-digit code from an authenticator app on your phone.
        {state.required ? ' Your organisation requires it for your role.' : ''}
      </p>
    );
    footer = (
      <>
        <button onClick={onClose}>Close</button>
        <button className="primary" onClick={start} disabled={busy}>Set up</button>
      </>
    );
  }

  return (
    <Modal title="Two-factor sign-in" onClose={onClose} footer={footer} wide={!!setup}>
      {body}
      <ErrorNote error={error} />
    </Modal>
  );
}

// ---------------------------------------------------------------- reveal with a reason

export type RevealSubject = 'principal' | 'request' | 'nomination';
export interface RevealedValues {
  identifier?: string;
  identifierType?: string;
  nomineeName?: string;
  nomineeContact?: string;
  nomineeContactType?: string;
}

const MIN_REASON = 10;

function useNow(active: boolean): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active]);
  return now;
}

function mmss(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * One record's contact details, masked until the user gives a reason. The
 * server opens a five-minute window for this user and this record; the
 * countdown here mirrors it and masks the values again when it ends.
 */
export function useReveal(subject: RevealSubject, id: string | null | undefined) {
  const [values, setValues] = useState<RevealedValues | null>(null);
  const [until, setUntil] = useState<number | null>(null);
  const [asking, setAsking] = useState(false);
  const now = useNow(until !== null);
  const allowed = can(getUser(), 'principals:reveal');

  // A window already open (the page was reloaded) is picked up again.
  useEffect(() => {
    setValues(null); setUntil(null);
    if (!id || !allowed) return;
    let cancelled = false;
    api.get<{ expiresAt: string; values: RevealedValues }>(`/v1/reveal/${subject}/${encodeURIComponent(id)}`)
      .then((r) => { if (!cancelled) { setValues(r.values); setUntil(Date.parse(r.expiresAt)); } })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [subject, id, allowed]);

  useEffect(() => {
    if (until !== null && now >= until) { setValues(null); setUntil(null); }
  }, [now, until]);

  const open = values && until !== null && now < until;
  const dialog = asking && id ? (
    <RevealDialog
      subject={subject} id={id}
      onClose={() => setAsking(false)}
      onRevealed={(v, exp) => { setValues(v); setUntil(Date.parse(exp)); setAsking(false); }}
    />
  ) : null;

  return {
    allowed,
    values: open ? values : null,
    left: open ? mmss(until! - now) : null,
    ask: () => setAsking(true),
    dialog,
  };
}

export type Reveal = ReturnType<typeof useReveal>;

function RevealDialog({
  subject, id, onClose, onRevealed,
}: { subject: RevealSubject; id: string; onClose: () => void; onRevealed: (v: RevealedValues, expiresAt: string) => void }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ready = reason.trim().length >= MIN_REASON;

  async function submit() {
    setBusy(true); setError(null);
    try {
      const r = await api.post<{ expiresAt: string; values: RevealedValues }>('/v1/reveal', { subject, id, reason });
      onRevealed(r.values, r.expiresAt);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Reveal contact details"
      description="Visible to you for 5 minutes. The reason is recorded in the audit log; the details are not."
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={submit} disabled={busy || !ready}>{busy ? 'Revealing…' : 'Reveal'}</button>
        </>
      }
    >
      <Field label="Reason" help={`At least ${MIN_REASON} characters. For example: calling the person back about their request.`}>
        <textarea rows={3} value={reason} autoFocus maxLength={500} onChange={(e) => setReason(e.target.value)} />
      </Field>
      <ErrorNote error={error} />
    </Modal>
  );
}

/** A masked value with its Reveal button, or the value with the time left. The page renders `reveal.dialog` once. */
export function RevealedField({
  reveal, value, masked, label = 'Hidden',
}: { reveal: Reveal; value: string | undefined; masked?: string | null; label?: string }) {
  if (reveal.values && value) {
    return (
      <span>
        <span className="mono">{value}</span>{' '}
        <span className="small muted" title="Masked again when the time runs out">Hidden in {reveal.left}</span>
      </span>
    );
  }
  return (
    <span>
      <span className="mono muted">{masked || label}</span>{' '}
      {reveal.allowed && <button className="small" onClick={reveal.ask}>Reveal</button>}
    </span>
  );
}

// ---------------------------------------------------------------- held for approval

/** The toast for a gated write: done, or sent to a second person. Returns true when held. */
export function useApprovalAware() {
  const toast = useToast();
  return useCallback((res: unknown, done: string): res is { approval: Approval } => {
    if (heldForApproval(res)) {
      toast('Sent for approval. A second person must approve this change.', 'ok');
      return true;
    }
    toast(done, 'ok');
    return false;
  }, [toast]);
}

export function SentForApproval({ approval }: { approval: Approval }) {
  return (
    <div className="note" role="status" style={{ marginTop: 8 }}>
      <strong>Sent for approval.</strong> {approval.summary}. A second person must approve it on the Approvals page.
    </div>
  );
}
